import { useState, useEffect } from 'react';

/**
 * @hook
 *
 * @summary
 * Custom React hook that debounces a changing value.
 *
 * @description
 * This hook returns a debounced version of the input `value`, which only updates
 * after the specified `delay` has elapsed since the last change. Useful for
 * performance optimization in cases like search inputs or API calls that should
 * not trigger on every keystroke.
 *
 * @template T
 * @param {T} value - The value to debounce. Can be any type.
 * @param {number} [delay=500] - The debounce delay in milliseconds. Defaults to 500ms.
 * @returns {T} The debounced value that updates after the delay period.
 *
 * @example
 * // Example usage:
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebounce(searchTerm, 300);
 *
 * useEffect(() => {
 *   if (debouncedSearchTerm) {
 *     fetchResults(debouncedSearchTerm);
 *   }
 * }, [debouncedSearchTerm]);
 */
export function useDebounce(value, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
